import React, { FormEvent, KeyboardEvent, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CircularProgress from "@mui/material/CircularProgress";

import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import EvStationIcon from "@mui/icons-material/EvStation";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SpeedIcon from "@mui/icons-material/Speed";
import BuildIcon from "@mui/icons-material/Build";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Co2Icon from "@mui/icons-material/Co2";
import CalculateIcon from "@mui/icons-material/Calculate";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { FleetFormData, calculateFleetReport } from "./calculateFleetReport";
import { savedFleetReport } from "../../features/fleet-report-module/fleetReportAction";
import { RootState, useAppDispatch, useAppSelector } from "../../stores";

import {
  getUtilityByZipCode,
  getScheduleByUtility,
  getEnergyBySchedule,
  Schedule,
  ScheduleDetailTipData,
  getScheduleDetailTip,
} from "../../features/generate-report-module/reportAction";
const SAFE_KWH_FLOOR = 0.18;
const API_MIN_THRESHOLD = 0.07;

// Styled components (same as your existing styling)
const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  background: "#fff",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(-1),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3f51b5",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: "28px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "20px",
  },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
  color: "#4b5b5d",
  fontSize: 22,
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    marginBottom: theme.spacing(1),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #455354)",
  color: "#fff",
  borderRadius: "8px",
  padding: theme.spacing(1, 5),
  fontSize: "16px",
  "&:hover": {
    background: "#354142",
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
    minHeight: "48px",
  },
}));

const HintText = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: "#757575",
  marginTop: theme.spacing(0.5),
}));

const DisclaimerText = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: "#666",
  fontStyle: "normal",
  marginTop: theme.spacing(2),
  padding: theme.spacing(1),
  backgroundColor: "#e2e2e2ff",
  borderRadius: "4px",
  borderLeft: "5px solid #ff9800",
}));

const FleetReports: React.FC = () => {
  const initialFormData: FleetFormData = {
    address: "",
    city: "",
    zipCode: "",
    fleetSize: "25",
    milesPerYear: "50000",
    iceMpg: "18",
    fuelPrice: "3.90",
    iceMaint: "1200",
    evMaint: "600",
    milesPerKwh: "3.0",
    elecPrice: "0.14",
    evPrice: "48000",
    incentive: "5000",
    resalePerVeh: "12000",
    infraTotal: "100000",
    years: "7",
    includeGridEmissions: false,
    gridIntensity: "0.40",
    applyTaxBenefits: false,
    businessTaxRate: "21",
    section179Limit: "2500000",
    bonusDepreciationPct: "0",
    qualifyingSpendOverride: "",
    disableUtilityEnergyInfo: false,
  };

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Redux state for utility data
  const {
    ratekWh,
    utilityLoading,
    scheduleLoading,
    energyLoading,
    utilityData,
    scheduleData,
    scheduleError,
    energyError,
    utilityError,
  } = useAppSelector((state: RootState) => state.report);

  const [formData, setFormData] = useState<FleetFormData>({
    ...initialFormData,
    disableUtilityEnergyInfo: false,
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [isZipApiComplete, setIsZipApiComplete] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(
    null
  );

  const [scheduleDetailData, setScheduleDetailData] = useState<any>(null);
  const [scheduleDetailLoading, setScheduleDetailLoading] = useState(false);
  const [scheduleDetailError, setScheduleDetailError] = useState<string | null>(
    null
  );

  // Helper to check if ZIP code is valid
  const hasValidZip = formData.zipCode && /^\d{5}$/.test(formData.zipCode);

  // Auto-fill electricity price when utility rate is available
useEffect(() => {
  if (ratekWh && !touched.elecPrice) {
    const apiRate = parseFloat(ratekWh);
    const safeRate =
      apiRate < API_MIN_THRESHOLD ? SAFE_KWH_FLOOR : apiRate;

    setFormData((prev) => ({
      ...prev,
      elecPrice: safeRate.toFixed(3),
    }));
  }
}, [ratekWh, touched.elecPrice]);


  // Fetch utility data when ZIP code changes
  useEffect(() => {
    if (formData.zipCode && /^\d{5}$/.test(formData.zipCode)) {
      dispatch(getUtilityByZipCode(formData.zipCode));
    }
  }, [formData.zipCode, dispatch]);

  // Fetch schedule and energy data when utility is available
  useEffect(() => {
    if (utilityData?.UtilityID) {
      dispatch(getScheduleByUtility(utilityData.UtilityID));
    }
  }, [utilityData, dispatch]);

  // Find valid schedule with positive ratekWh (similar to generate report but without getScheduleDetailTip)
  useEffect(() => {
    const findValidSchedule = async () => {
      if (
        !hasValidZip ||
        !scheduleData ||
        scheduleData.length === 0 ||
        scheduleError
      )
        return;

      for (const sched of scheduleData) {
        try {
          const energyRes = await dispatch(
            getEnergyBySchedule(sched.ScheduleID)
          ).unwrap();

          if (
            energyRes.success &&
            energyRes.data?.ratekWh &&
            parseFloat(energyRes.data.ratekWh) > 0
          ) {
            setSelectedSchedule(sched);
            return;
          }
        } catch (err) {
          console.error(`Error for schedule ${sched.ScheduleID}:`, err);
        }
      }

      setSelectedSchedule(null);
      dispatch({
        type: "report/getEnergyBySchedule/rejected",
        payload: "No valid rate found",
      });
    };

    findValidSchedule();
  }, [scheduleData, scheduleError, dispatch, hasValidZip]);

  useEffect(() => {
    const fetchScheduleDetails = async () => {
      if (selectedSchedule?.ScheduleID) {
        setScheduleDetailLoading(true);
        setScheduleDetailError(null);
        try {
          const result = await dispatch(
            getScheduleDetailTip(selectedSchedule.ScheduleID)
          ).unwrap();
          if (result.success && result.data) {
            setScheduleDetailData(result.data);
          }
        } catch (error) {
          setScheduleDetailError("Failed to fetch schedule details");
          console.error("Error fetching schedule details:", error);
        } finally {
          setScheduleDetailLoading(false);
        }
      }
    };

    fetchScheduleDetails();
  }, [selectedSchedule, dispatch]);

  // Check if ZIP API process is complete
  useEffect(() => {
    if (hasValidZip) {
      if (
        !utilityLoading &&
        !scheduleLoading &&
        !energyLoading &&
        !scheduleDetailLoading &&
        (ratekWh ||
          utilityError ||
          scheduleError ||
          energyError ||
          scheduleDetailError)
      ) {
        setIsZipApiComplete(true);
      } else {
        setIsZipApiComplete(false);
      }
    } else {
      setIsZipApiComplete(false);
    }
  }, [
    hasValidZip,
    utilityLoading,
    scheduleLoading,
    energyLoading,
    scheduleDetailLoading,
    ratekWh,
    utilityError,
    scheduleError,
    energyError,
    scheduleDetailError,
  ]);

  // Google Maps Autocomplete
  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDZsmG9zzmlEZLY3BpPYeJMfUphIobw-sM&libraries=places`;
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      // Address autocomplete
      const addressInput = document.getElementById(
        "fleet-address-input"
      ) as HTMLInputElement;
      if (addressInput) {
        const addressAutocomplete = new (
          window as any
        ).google.maps.places.Autocomplete(addressInput, {
          types: ["address"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry", "formatted_address"],
        });

        addressAutocomplete.addListener("place_changed", () => {
          const place = addressAutocomplete.getPlace();
          const components = place.address_components || [];
          let city = "";
          let zipCode = "";

          components.forEach((cmp: any) => {
            if (cmp.types.includes("locality")) city = cmp.long_name;
            if (cmp.types.includes("postal_code")) zipCode = cmp.long_name;
          });

          setFormData((prev) => ({
            ...prev,
            address: place.formatted_address || prev.address,
            city: city || prev.city,
            zipCode: zipCode || prev.zipCode,
          }));
        });
      }

      // ZIP code autocomplete
      const zipInput = document.getElementById(
        "fleet-zipcode-input"
      ) as HTMLInputElement;
      if (zipInput) {
        const zipAutocomplete = new (
          window as any
        ).google.maps.places.Autocomplete(zipInput, {
          types: ["(regions)"],
          componentRestrictions: { country: "us" },
          fields: ["address_components", "geometry", "formatted_address"],
        });

        zipAutocomplete.addListener("place_changed", () => {
          const place = zipAutocomplete.getPlace();
          const components = place.address_components || [];
          let city = "";
          let address = "";
          let zipCode = "";

          components.forEach((cmp: any) => {
            if (cmp.types.includes("locality")) city = cmp.long_name;
            if (cmp.types.includes("postal_code")) zipCode = cmp.long_name;
          });

          address = place.formatted_address || city;

          setFormData((prev) => ({
            ...prev,
            address: address || prev.address,
            city: city || prev.city,
            zipCode: zipCode || prev.zipCode,
          }));
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Reset form on component mount
  useEffect(() => {
    setFormData(initialFormData);
    setTouched({});
    setErrors({});
    setLoading(false);
  }, []);

  const handleChange = (e: any, child?: React.ReactNode) => {
    const name = e.target.name;
    let value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    // Validation for zip code and numeric fields
    if (typeof value === "string") {
      if (
        [
          "zipCode",
          "fleetSize",
          "milesPerYear",
          "iceMpg",
          "fuelPrice",
          "iceMaint",
          "evMaint",
          "milesPerKwh",
          "elecPrice",
          "evPrice",
          "incentive",
          "resalePerVeh",
          "infraTotal",
          "gridIntensity",
          "businessTaxRate",
          "section179Limit",
          "bonusDepreciationPct",
          "qualifyingSpendOverride",
        ].includes(name) &&
        value.includes(" ")
      ) {
        let fieldLabel = "";
        switch (name) {
          case "zipCode":
            fieldLabel = "Zip Code";
            break;
          case "fleetSize":
            fieldLabel = "Fleet Size";
            break;
          case "milesPerYear":
            fieldLabel = "Miles per Year";
            break;
          case "iceMpg":
            fieldLabel = "ICE MPG";
            break;
          case "fuelPrice":
            fieldLabel = "Fuel Price";
            break;
          case "iceMaint":
            fieldLabel = "ICE Maintenance";
            break;
          case "evMaint":
            fieldLabel = "EV Maintenance";
            break;
          case "milesPerKwh":
            fieldLabel = "Miles per kWh";
            break;
          case "elecPrice":
            fieldLabel = "Electricity Price";
            break;
          case "evPrice":
            fieldLabel = "EV Price";
            break;
          case "incentive":
            fieldLabel = "Incentive";
            break;
          case "resalePerVeh":
            fieldLabel = "Resale Value";
            break;
          case "infraTotal":
            fieldLabel = "Infrastructure Total";
            break;
          case "gridIntensity":
            fieldLabel = "Grid Intensity";
            break;
          case "householdIncome":
            fieldLabel = "Household Income";
            break;
          default:
            fieldLabel = name;
        }
        toast.error(`${fieldLabel} cannot contain spaces`);
        return;
      }

      if (name === "zipCode") {
        if (!/^\d*$/.test(value)) {
          toast.error("Zip Code can only contain numbers (0-9)");
          return;
        }
        if (value.length > 5) {
          toast.error("Zip Code must be exactly 5 digits");
          return;
        }
        if (value.length <= 5) {
          setErrors((prev) => ({
            ...prev,
            zipCode: "",
          }));
        }
      }

      if (
        [
          "fleetSize",
          "milesPerYear",
          "iceMpg",
          "fuelPrice",
          "iceMaint",
          "evMaint",
          "milesPerKwh",
          "elecPrice",
          "evPrice",
          "incentive",
          "resalePerVeh",
          "infraTotal",
          "gridIntensity",
          "businessTaxRate",
          "section179Limit",
          "bonusDepreciationPct",
          "qualifyingSpendOverride",
        ].includes(name) &&
        value &&
        !/^\d*\.?\d*$/.test(value)
      ) {
        toast.error("Please enter a valid number");
        return;
      }

      if (
        [
          "fleetSize",
          "milesPerYear",
          "iceMpg",
          "fuelPrice",
          "iceMaint",
          "evMaint",
          "milesPerKwh",
          "elecPrice",
          "evPrice",
          "incentive",
          "resalePerVeh",
          "infraTotal",
          "gridIntensity",
          "businessTaxRate",
          "section179Limit",
          "bonusDepreciationPct",
          "qualifyingSpendOverride",
        ].includes(name) &&
        value &&
        parseFloat(value) < 0
      ) {
        toast.error("Value cannot be negative");
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (typeof value === "string") {
      const message = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: message }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    if (
      name === "address" ||
      name === "city" ||
      name === "zipCode" ||
      name === "fleetSize" ||
      name === "milesPerYear" ||
      name === "iceMpg" ||
      name === "fuelPrice" ||
      name === "iceMaint" ||
      name === "evMaint" ||
      name === "milesPerKwh" ||
      name === "elecPrice" ||
      name === "evPrice" ||
      name === "incentive" ||
      name === "resalePerVeh" ||
      name === "infraTotal" ||
      name === "years" ||
      name === "gridIntensity" ||
      name === "businessTaxRate" ||
      name === "section179Limit" ||
      name === "bonusDepreciationPct" ||
      name === "qualifyingSpendOverride"
    ) {
      const message = validateField(name, value as string);
      setErrors((prev) => ({ ...prev, [name]: message }));
    }
  };

  const validateField = (name: string, value: string) => {
    const requiredFields = [
      "address",
      "city",
      "zipCode",
      "fleetSize",
      "milesPerYear",
      "iceMpg",
      "fuelPrice",
      "iceMaint",
      "evMaint",
      "milesPerKwh",
      "elecPrice",
      "evPrice",
      "incentive",
      "resalePerVeh",
      "years",
      "gridIntensity",
    ];

    // Tax fields that are required when tax benefits are enabled
    const taxRequiredFields = [
      "businessTaxRate",
      "section179Limit",
      "bonusDepreciationPct",
    ];

    if (requiredFields.includes(name) && !value.trim()) {
      return "This field is required";
    }

    // Validate tax fields only when tax benefits are enabled
    if (
      formData.applyTaxBenefits &&
      taxRequiredFields.includes(name) &&
      !value.trim()
    ) {
      return "This field is required";
    }

    if (name === "zipCode") {
      if (value && !/^\d+$/.test(value)) {
        return "Zip Code can only contain numbers (0-9)";
      }
      if (value && value.length !== 5) {
        return "Zip Code must be exactly 5 digits";
      }
    }

    if (
      [
        "fleetSize",
        "milesPerYear",
        "iceMpg",
        "fuelPrice",
        "iceMaint",
        "evMaint",
        "milesPerKwh",
        "elecPrice",
        "evPrice",
        "incentive",
        "resalePerVeh",
        "infraTotal",
        "gridIntensity",
        "businessTaxRate",
        "section179Limit",
        "bonusDepreciationPct",
        "qualifyingSpendOverride",
      ].includes(name) &&
      value &&
      !/^\d*\.?\d*$/.test(value)
    ) {
      return "Please enter a valid number";
    }

    if (
      [
        "fleetSize",
        "milesPerYear",
        "iceMpg",
        "fuelPrice",
        "iceMaint",
        "evMaint",
        "milesPerKwh",
        "elecPrice",
        "evPrice",
        "incentive",
        "resalePerVeh",
        "infraTotal",
        "gridIntensity",
        "businessTaxRate",
        "section179Limit",
        "bonusDepreciationPct",
        "qualifyingSpendOverride",
      ].includes(name) &&
      value &&
      parseFloat(value) < 0
    ) {
      return "Value cannot be negative";
    }

    // Tax benefits validation
    if (name === "businessTaxRate" && value) {
      const rate = parseFloat(value);
      if (isNaN(rate) || rate < 0 || rate > 100) {
        return "Business tax rate must be between 0 and 100";
      }
    }

    if (name === "section179Limit" && value) {
      const limit = parseFloat(value);
      if (isNaN(limit) || limit < 0) {
        return "Section 179 limit must be a positive number";
      }
    }

    if (name === "bonusDepreciationPct" && value) {
      const pct = parseFloat(value);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        return "Bonus depreciation must be between 0 and 100";
      }
    }

    if (name === "qualifyingSpendOverride" && value) {
      const spend = parseFloat(value);
      if (isNaN(spend) || spend < 0) {
        return "Qualifying spend must be a positive number";
      }
    }

    return "";
  };

  const validateAll = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    const requiredFields = [
      "address",
      "city",
      "zipCode",
      "fleetSize",
      "milesPerYear",
      "iceMpg",
      "fuelPrice",
      "iceMaint",
      "evMaint",
      "milesPerKwh",
      "elecPrice",
      "evPrice",
      "incentive",
      "resalePerVeh",
      "years",
      "gridIntensity",
    ];

    requiredFields.forEach((field) => {
      newErrors[field] = validateField(
        field,
        formData[field as keyof FleetFormData] as string
      );
    });

    // Validate tax fields if tax benefits are enabled
    if (formData.applyTaxBenefits) {
      const taxFields = [
        "businessTaxRate",
        "section179Limit",
        "bonusDepreciationPct",
      ];

      taxFields.forEach((field) => {
        newErrors[field] = validateField(
          field,
          formData[field as keyof FleetFormData] as string
        );
      });
    }

    // Validate zip code format
    if (formData.zipCode) {
      if (!/^\d+$/.test(formData.zipCode) || formData.zipCode.length !== 5) {
        newErrors.zipCode =
          formData.zipCode.length !== 5
            ? "Zip Code must be exactly 5 digits"
            : "Zip Code can only contain numbers (0-9)";
      }
    }

    // Clean up empty errors
    Object.keys(newErrors).forEach((k) => {
      if (!newErrors[k]) delete newErrors[k];
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));

    // Mark all relevant fields as touched
    setTouched((prev) => {
      const newTouched = { ...prev };
      const allFieldsToTouch = [
        ...requiredFields,
        ...(formData.applyTaxBenefits
          ? ["businessTaxRate", "section179Limit", "bonusDepreciationPct"]
          : []),
      ];

      allFieldsToTouch.forEach((field) => {
        newTouched[field] = true;
      });

      return newTouched;
    });

    return Object.keys(newErrors).length === 0;
  };

  const calculateFleetEnergyCosts = (
    scheduleDetailData: ScheduleDetailTipData | null,
    baseRate: number,
    fleetSize: number,
    milesPerYear: number,
    milesPerKwh: number,
    analysisYears: number = 7
  ) => {
    const zeros = Array(analysisYears).fill(0);

    if (!scheduleDetailData) {
      return {
        energy: zeros,
        energyTime: zeros,
        incrementalEnergy: zeros,
        demand: zeros,
        demandTime: zeros,
        incrementalDemand: zeros,
        serviceCharge: zeros,
        otherCharges: zeros,
        total: zeros,
      };
    }

    // Calculate annual kWh consumption for the fleet
    const kwhPerVehPerYr = milesPerYear / Math.max(milesPerKwh, 0.0001);
    const annualKwh = kwhPerVehPerYr * fleetSize;
    const kWhYearArr = Array(analysisYears).fill(annualKwh);

    // Helper functions
    const toNum = (v: any) => {
      const n = parseFloat(
        String(v ?? "")
          .toString()
          .replace(/[^0-9.-]/g, "")
      );
      return Number.isFinite(n) ? n : 0;
    };

    const toNumStrict = (v: any) => {
      const s = String(v ?? "").trim();
      if (/[a-zA-Z]/.test(s)) return NaN;
      const n = parseFloat(s.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(n) ? n : NaN;
    };

    // 1) ENERGY: simple baseline using fetched rate
    const energy = kWhYearArr.map((x) => x * (baseRate || 0));

    // 2) DEMAND: conservative monthly generic demand charge
    let monthlyDemandRate = 0;
    const Demand_Table = scheduleDetailData?.Demand_Table || [];

    for (const d of Demand_Table) {
      const unit = String(d.ChargeUnit || "").toLowerCase();
      const determinant = String(d.Determinant || "").toLowerCase();
      const rate = toNumStrict(d.RatekW);
      const looksMonthlyKW =
        unit.includes("month") && determinant.includes("kw");
      const generic = !/4cp|tou|on[-\s]?peak|off[-\s]?peak|summer|winter/.test(
        determinant
      );
      if (looksMonthlyKW && generic && rate > 0) {
        monthlyDemandRate = Math.max(monthlyDemandRate, rate);
      }
    }

    // Estimate demand for fleet charging - conservative approach
    // Assuming Level 2 chargers with average 7kW per vehicle and 25% concurrency
    const avgChargerPower = 7; // kW per charger
    const concurrency = 0.25; // 25% of fleet charging simultaneously
    const billableKW = Math.max(30, fleetSize * avgChargerPower * concurrency);

    const demandAnnual = monthlyDemandRate * billableKW * 12;
    const demand = Array(analysisYears).fill(demandAnnual);

    // 3) SERVICE CHARGE: sum $/month rows, annualize
    let monthlyService = 0;
    const ServiceCharge_Table = scheduleDetailData?.ServiceCharge_Table || [];
    for (const sc of ServiceCharge_Table) {
      const unit = String(sc.ChargeUnit || "").toLowerCase();
      const rate = toNum(sc.Rate);
      if (unit.includes("month") && rate > 0) monthlyService += rate;
    }
    const serviceAnnual = monthlyService * 12;
    const serviceCharge = Array(analysisYears).fill(serviceAnnual);

    // 4) OTHER CHARGES: conservative (ignore unless clearly $/month)
    let monthlyOther = 0;
    const OtherCharges_Table = scheduleDetailData?.OtherCharges_Table || [];
    for (const oc of OtherCharges_Table) {
      const unit = String(oc.ChargeUnit || "").toLowerCase();
      const rate = toNumStrict((oc as any).Rate);
      if (unit.includes("month") && Number.isFinite(rate) && rate > 0) {
        monthlyOther += rate;
      }
    }

    const otherAnnual = monthlyOther * 12;
    const otherCharges = Array(analysisYears).fill(otherAnnual);

    // Ignore TOU & incremental until we have load shapes:
    const energyTime = zeros;
    const incrementalEnergy = zeros;
    const demandTime = zeros;
    const incrementalDemand = zeros;

    // Total by year
    const total = energy.map(
      (e, i) => e + demand[i] + serviceCharge[i] + otherCharges[i]
    );

    return {
      energy,
      energyTime,
      incrementalEnergy,
      demand,
      demandTime,
      incrementalDemand,
      serviceCharge,
      otherCharges,
      total,
    };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isValid = validateAll();
    if (!isValid) {
      toast.error("Please fill in the required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Use utility rate if available, otherwise use form value
const rawUtilityRate = ratekWh
  ? parseFloat(ratekWh)
  : parseFloat(formData.elecPrice);

const utilityRate =
  rawUtilityRate < API_MIN_THRESHOLD
    ? SAFE_KWH_FLOOR
    : rawUtilityRate;

      // Calculate the report data
      const reportData = calculateFleetReport(formData, utilityRate);

      // Calculate energy costs
      const energyCosts = calculateFleetEnergyCosts(
        scheduleDetailData,
        utilityRate,
        parseInt(formData.fleetSize),
        parseFloat(formData.milesPerYear),
        parseFloat(formData.milesPerKwh),
        parseInt(formData.years)
      );

      // Prepare utility details
      const utilityDetails = utilityData
        ? {
            state: utilityData.State,
            zipCode: formData.zipCode,
            utilityName: utilityData.UtilityName,
            utilityId: utilityData.UtilityID,
            scheduleId: selectedSchedule?.ScheduleID || "",
            totalCost: energyCosts.total
              .reduce((sum, x) => sum + x, 0)
              .toFixed(2),
          }
        : undefined;

      // Prepare report for saving with all data
      const savedReport = {
        reportName: `${formData.fleetSize} Fleet size - ${formData.city}, ${formData.zipCode}`,
        location: formData.address,
        city: formData.city,
        zipCode: formData.zipCode,
        fleetSize: parseInt(formData.fleetSize),
        netSavings: reportData.netSavings,
        utilityRate: reportData.utilityRate,
        disableUtilityEnergyInfo: formData.disableUtilityEnergyInfo,

        // Include utility details and energy costs
        utilityDetails,
        energyCosts,

        // Financial data
        financialTable: [
          {
            category: "Annual ICE Fuel Cost",
            value: `$${reportData.iceFuelCostAnnual.toLocaleString()}`,
          },
          {
            category: "Annual EV Energy Cost",
            value: `$${reportData.evEnergyCostAnnual.toLocaleString()}`,
          },
          {
            category: "Annual ICE Maintenance",
            value: `$${reportData.iceMaintAnnual.toLocaleString()}`,
          },
          {
            category: "Annual EV Maintenance",
            value: `$${reportData.evMaintAnnual.toLocaleString()}`,
          },
          {
            category: "Annual ICE Opex",
            value: `$${reportData.iceOpexAnnual.toLocaleString()}`,
          },
          {
            category: "Annual EV Opex",
            value: `$${reportData.evOpexAnnual.toLocaleString()}`,
          },
          {
            category: "Annual Savings",
            value: `$${reportData.annualSavings.toLocaleString()}`,
          },
          {
            category: "Upfront EVs Cost",
            value: `$${reportData.upfrontEVs.toLocaleString()}`,
          },
          {
            category: "Incentives Total",
            value: `$${reportData.incentives.toLocaleString()}`,
          },
          {
            category: "Resale Value Total",
            value: `$${reportData.resaleTotal.toLocaleString()}`,
          },
          {
            category: "Infrastructure Cost",
            value: `$${parseFloat(formData.infraTotal).toLocaleString()}`,
          },
          {
            category: "Upfront Net Outlay",
            value: `$${reportData.upfrontNet.toLocaleString()}`,
          },
          // Tax benefits row if enabled
          ...(reportData.applyTaxBenefits
            ? [
                {
                  category: "Section 179 / 168(k) Tax Savings (Est.)",
                  value: `$${reportData.estTaxSavings.toLocaleString()}`,
                },
              ]
            : []),
        ],

        // Tax benefits table
        taxBenefitsTable: reportData.applyTaxBenefits
          ? [
              {
                category: "Business Tax Rate",
                value: `${reportData.businessTaxRate}%`,
              },
              {
                category: "Section 179 Limit",
                value: `$${reportData.section179Limit.toLocaleString()}`,
              },
              {
                category: "Bonus Depreciation %",
                value: `${reportData.bonusDepreciationPct}%`,
              },
              {
                category: "Qualifying Spend",
                value: `$${reportData.qualifyingSpend.toLocaleString()}`,
              },
              {
                category: "Est. Section 179 Deduction",
                value: `$${reportData.est179Deduction.toLocaleString()}`,
              },
              {
                category: "Est. 168(k) Deduction",
                value: `$${reportData.est168kDeduction.toLocaleString()}`,
              },
              {
                category: "Estimated Tax Savings",
                value: `$${reportData.estTaxSavings.toLocaleString()}`,
              },
            ]
          : [],

        // Environmental data
        environmentalTable: [
          {
            category: "Annual Gasoline Avoided",
            value: `${reportData.gallonsAvoidedAnnual.toLocaleString()} gal`,
          },
          {
            category: "Annual CO2 Offset (Tailpipe)",
            value: `${reportData.co2AnnualKgTailpipe.toLocaleString()} kg`,
          },
          {
            category: "Annual Grid Emissions",
            value: `${reportData.gridEmissionsAnnual.toLocaleString()} kg`,
          },
          {
            category: "Annual Net CO2",
            value: `${reportData.co2AnnualKgNet.toLocaleString()} kg`,
          },
          {
            category: `${formData.years}-Year Net CO2`,
            value: `${reportData.co2NetTons.toLocaleString()} tons`,
          },
        ],

        // Chart data
        costOverTimeData: {
          labels: reportData.yearsArray.map((year) => `Year ${year}`),
          datasets: [
            { label: "ICE Opex", data: [0, ...reportData.costOverTimeIce] },
            { label: "EV Opex", data: [0, ...reportData.costOverTimeEv] },
          ],
        },
        cumulativeSavingsData: {
          labels: reportData.yearsArray.map((year) => `Year ${year}`),
          data: reportData.cumulativeNetSavings,
        },
        environmentalData: {
          labels: [
            "Gasoline Avoided (gal)",
            "Tailpipe CO2 Avoided (kg)",
            "Net CO2 (kg)",
          ],
          data: [
            reportData.gallonsAvoidedAnnual,
            reportData.co2AnnualKgTailpipe,
            reportData.co2AnnualKgNet,
          ],
        },

        // Additional metadata
        paybackPeriod: reportData.paybackYears,
        analysisYears: parseInt(formData.years),
        applyTaxBenefits: reportData.applyTaxBenefits,
        timestamp: new Date().toISOString(),
      };

      const result = await dispatch(savedFleetReport(savedReport)).unwrap();

      const createdReportId = result.data?.report?._id;

      const savedReportWithId = {
        ...savedReport,
        _id: createdReportId,
      };

      navigate("/main/fleet-reports/view", {
        state: {
          formData,
          reportData,
          savedReport: savedReportWithId,
          utilityDetails,
          energyCosts,
          disableUtilityEnergyInfo: formData.disableUtilityEnergyInfo,
        },
      });
    } catch (error) {
      console.error("Error generating fleet report:", error);
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={handleSubmit}
            onKeyDown={handleKeyPress}
          >
            <WelcomeText>Fleet EV Conversion Savings Estimator</WelcomeText>

            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                padding: 2.5,
                mt: 2.5,
                borderRadius: 2,
              }}
            >
              {/* Location Section */}
              <SubHeading>Location Details</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Address*</FieldLabel>
                  <StyledTextField
                    id="fleet-address-input"
                    name="address"
                    variant="outlined"
                    type="text"
                    value={formData.address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.address && errors.address)}
                    helperText={
                      touched.address && errors.address ? errors.address : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>City*</FieldLabel>
                  <StyledTextField
                    name="city"
                    variant="outlined"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.city && errors.city)}
                    helperText={touched.city && errors.city ? errors.city : ""}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Zip Code*</FieldLabel>
                  <StyledTextField
                    id="fleet-zipcode-input"
                    name="zipCode"
                    variant="outlined"
                    type="text"
                    value={formData.zipCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.zipCode && errors.zipCode)}
                    helperText={
                      touched.zipCode && errors.zipCode
                        ? errors.zipCode
                        : utilityLoading || scheduleLoading || energyLoading
                        ? "Fetching utility rate..."
              : ratekWh
? `Utility Rate: ${Math.abs(parseFloat(ratekWh)).toFixed(
    4
  )} $/kWh${
    selectedSchedule
      ? ` (${selectedSchedule.ScheduleName})`
      : ""
  }`

                        : utilityError || scheduleError || energyError
                        ? "Failed to fetch utility rate"
                        : "Enter 5-digit zip code (numbers only)"
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{
                      maxLength: 5,
                      inputMode: "numeric",
                      pattern: "[0-9]{5}",
                      placeholder: "12345",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon />
                        </InputAdornment>
                      ),
                      endAdornment:
                        utilityLoading || scheduleLoading || energyLoading ? (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ) : null,
                    }}
                  />
                </Grid>
              </Grid>

              {/* Add this checkbox right after Location Details */}
              <Grid container spacing={2} sx={{ width: "100%", mb: 2 }}>
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      userSelect: "none",
                      justifyContent: "flex-start",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="disableUtilityEnergyInfo"
                      name="disableUtilityEnergyInfo"
                      checked={formData.disableUtilityEnergyInfo || false}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          disableUtilityEnergyInfo: e.target.checked,
                        }))
                      }
                      style={{
                        marginRight: "5px",
                        width: "16px",
                        height: "16px",
                        color: "#4b5b5d",
                      }}
                    />
                    <label
                      htmlFor="disableUtilityEnergyInfo"
                      style={{
                        fontSize: "0.9rem",
                        color: "#455354",
                        fontWeight: 500,
                      }}
                    >
                      Exclude Demand Charges
                    </label>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", ml: 3, display: "block" }}
                  >
                    Check this box if your site is already subject to demand
                    charges, is near the threshold for incurring them, or has a
                    negotiated rate structure that avoids them. Selecting this
                    will remove demand charges from the revenue estimate.
                  </Typography>
                </Grid>
              </Grid>

              <SubHeading>Fleet Configuration</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Fleet Size (vehicles)*</FieldLabel>
                  <StyledTextField
                    name="fleetSize"
                    variant="outlined"
                    type="number"
                    value={formData.fleetSize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.fleetSize && errors.fleetSize)}
                    helperText={
                      touched.fleetSize && errors.fleetSize
                        ? errors.fleetSize
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DirectionsCarIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Miles per Vehicle / Year*</FieldLabel>
                  <StyledTextField
                    name="milesPerYear"
                    variant="outlined"
                    type="number"
                    value={formData.milesPerYear}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.milesPerYear && errors.milesPerYear)}
                    helperText={
                      touched.milesPerYear && errors.milesPerYear
                        ? errors.milesPerYear
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <SubHeading>ICE Vehicle Parameters</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>ICE MPG (avg)*</FieldLabel>
                  <StyledTextField
                    name="iceMpg"
                    variant="outlined"
                    type="number"
                    value={formData.iceMpg}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.iceMpg && errors.iceMpg)}
                    helperText={
                      touched.iceMpg && errors.iceMpg ? errors.iceMpg : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "1", step: "0.1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocalGasStationIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Fuel Price ($/gallon)*</FieldLabel>
                  <StyledTextField
                    name="fuelPrice"
                    variant="outlined"
                    type="number"
                    value={formData.fuelPrice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.fuelPrice && errors.fuelPrice)}
                    helperText={
                      touched.fuelPrice && errors.fuelPrice
                        ? errors.fuelPrice
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>ICE Maintenance ($/vehicle/yr)*</FieldLabel>
                  <StyledTextField
                    name="iceMaint"
                    variant="outlined"
                    type="number"
                    value={formData.iceMaint}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.iceMaint && errors.iceMaint)}
                    helperText={
                      touched.iceMaint && errors.iceMaint ? errors.iceMaint : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BuildIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <SubHeading>EV Vehicle Parameters</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>EV Efficiency (miles/kWh)*</FieldLabel>
                  <StyledTextField
                    name="milesPerKwh"
                    variant="outlined"
                    type="number"
                    value={formData.milesPerKwh}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.milesPerKwh && errors.milesPerKwh)}
                    helperText={
                      touched.milesPerKwh && errors.milesPerKwh
                        ? errors.milesPerKwh
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0.1", step: "0.01" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EvStationIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Electricity Cost Per kWh*</FieldLabel>
                  <StyledTextField
                    name="elecPrice"
                    variant="outlined"
                    type="number"
                    value={formData.elecPrice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.elecPrice && errors.elecPrice)}
                    helperText={
                      touched.elecPrice && errors.elecPrice
                        ? errors.elecPrice
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.001" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>EV Maintenance ($/vehicle/yr)*</FieldLabel>
                  <StyledTextField
                    name="evMaint"
                    variant="outlined"
                    type="number"
                    value={formData.evMaint}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.evMaint && errors.evMaint)}
                    helperText={
                      touched.evMaint && errors.evMaint ? errors.evMaint : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "1" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BuildIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <SubHeading>Financial Parameters</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>EV Purchase Price ($/vehicle)*</FieldLabel>
                  <StyledTextField
                    name="evPrice"
                    variant="outlined"
                    type="number"
                    value={formData.evPrice}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.evPrice && errors.evPrice)}
                    helperText={
                      touched.evPrice && errors.evPrice ? errors.evPrice : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "100" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>Incentives/Rebates ($/vehicle)*</FieldLabel>
                  <StyledTextField
                    name="incentive"
                    variant="outlined"
                    type="number"
                    value={formData.incentive}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.incentive && errors.incentive)}
                    helperText={
                      touched.incentive && errors.incentive
                        ? errors.incentive
                        : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "100" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FieldLabel>
                    Resale Value of Current Vehicle ($/vehicle)*
                  </FieldLabel>
                  <StyledTextField
                    name="resalePerVeh"
                    variant="outlined"
                    type="number"
                    value={formData.resalePerVeh}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.resalePerVeh && errors.resalePerVeh)}
                    helperText={
                      touched.resalePerVeh && errors.resalePerVeh
                        ? errors.resalePerVeh
                        : "Reduces your upfront outlay"
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "100" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Charging Infrastructure (Total $)</FieldLabel>
                  <StyledTextField
                    name="infraTotal"
                    variant="outlined"
                    type="number"
                    value={formData.infraTotal}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "500" }}
                    helperText="Optional upfront cost (site work, hardware, install)"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>Analysis Period (years)*</FieldLabel>
                  <StyledTextField
                    select
                    name="years"
                    variant="outlined"
                    value={formData.years}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(touched.years && errors.years)}
                    helperText={
                      touched.years && errors.years ? errors.years : ""
                    }
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon />
                        </InputAdornment>
                      ),
                    }}
                  >
                    <MenuItem value="5">5 years</MenuItem>
                    <MenuItem value="7">7 years</MenuItem>
                    <MenuItem value="10">10 years</MenuItem>
                  </StyledTextField>
                </Grid>
              </Grid>

              <SubHeading>Tax Deduction Settings</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="applyTaxBenefits"
                        checked={formData.applyTaxBenefits}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Apply Section 179 & 168(k) bonus depreciation?"
                  />
                  <HintText>
                    Uses configurable limits and bonus % — consult your CPA.
                  </HintText>
                </Grid>

                {formData.applyTaxBenefits && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Business Tax Rate (%)*</FieldLabel>
                      <StyledTextField
                        name="businessTaxRate"
                        variant="outlined"
                        type="number"
                        value={formData.businessTaxRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        error={Boolean(
                          touched.businessTaxRate && errors.businessTaxRate
                        )}
                        helperText={
                          touched.businessTaxRate && errors.businessTaxRate
                            ? errors.businessTaxRate
                            : "Approx. federal corporate rate; adjust if needed."
                        }
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", max: "100", step: "0.1" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalculateIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Section 179 Limit ($)*</FieldLabel>
                      <StyledTextField
                        name="section179Limit"
                        variant="outlined"
                        type="number"
                        value={formData.section179Limit}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        error={Boolean(
                          touched.section179Limit && errors.section179Limit
                        )}
                        helperText={
                          touched.section179Limit && errors.section179Limit
                            ? errors.section179Limit
                            : "Default 2025 limit; phase-out rules apply at higher spend."
                        }
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", step: "1000" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Bonus Depreciation % (168(k))*</FieldLabel>
                      <StyledTextField
                        name="bonusDepreciationPct"
                        variant="outlined"
                        type="number"
                        value={formData.bonusDepreciationPct}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        error={Boolean(
                          touched.bonusDepreciationPct &&
                            errors.bonusDepreciationPct
                        )}
                        helperText={
                          touched.bonusDepreciationPct &&
                          errors.bonusDepreciationPct
                            ? errors.bonusDepreciationPct
                            : "Percent of remaining qualifying spend above the 179 limit."
                        }
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", max: "100", step: "1" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalculateIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Override Qualifying Spend ($)</FieldLabel>
                      <StyledTextField
                        name="qualifyingSpendOverride"
                        variant="outlined"
                        type="number"
                        value={formData.qualifyingSpendOverride}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: "0", step: "1000" }}
                        helperText="If blank, we use EV purchases + charging infrastructure as qualifying spend"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}

                {formData.applyTaxBenefits && (
                  <DisclaimerText>
                    <strong>Tax disclaimer:</strong> Estimates are for
                    illustration only and do not constitute tax advice. Section
                    179 and 168(k) eligibility and limits vary by entity and
                    state. Consult a qualified tax professional to confirm
                    treatment for your situation.
                  </DisclaimerText>
                )}
              </Grid>

              <SubHeading>Environmental Parameters</SubHeading>
              <Grid container spacing={2} sx={{ width: "100%", mb: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="includeGridEmissions"
                        checked={formData.includeGridEmissions}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="Include Grid Emissions (Net CO₂)?"
                  />
                  <HintText>
                    Subtract EV charging emissions from tailpipe CO₂ avoided.
                  </HintText>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FieldLabel>
                    Grid Emissions Intensity (kg CO₂/kWh)*
                  </FieldLabel>
                  <StyledTextField
                    name="gridIntensity"
                    variant="outlined"
                    type="number"
                    value={formData.gridIntensity}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(
                      touched.gridIntensity && errors.gridIntensity
                    )}
                    helperText={
                      touched.gridIntensity && errors.gridIntensity
                        ? errors.gridIntensity
                        : "Approximate U.S. average: 0.35–0.45"
                    }
                    size={isMobile ? "small" : "medium"}
                    inputProps={{ min: "0", step: "0.001" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Co2Icon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 2,
                }}
              >
                <motion.div
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <PrimaryButton
                    type="submit"
                    variant="contained"
                    disabled={
                      loading ||
                      utilityLoading ||
                      scheduleLoading ||
                      energyLoading ||
                      !isZipApiComplete
                    }
                    startIcon={
                      loading ||
                      utilityLoading ||
                      scheduleLoading ||
                      energyLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ||
                    utilityLoading ||
                    scheduleLoading ||
                    energyLoading
                      ? "Calculating..."
                      : "Calculate Savings"}
                  </PrimaryButton>
                </motion.div>
              </Box>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default FleetReports;
